import * as React from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { translateCountryName } from '@/app/common/countryNames'
import { DateField } from '@/ui/form/controls/DateField'
import { Dropdown } from '@/ui/form/controls/Dropdown'
import { useQueryCountries } from '@/modules/shared/query-client/useQueryCountries'
import { useQueryVehiclesList } from '@/lib/api/company/vehicle-list/useQueryVehiclesList'
import { VehiclesResponse } from '@/lib/api/company/vehicle-list/types'
import { TmsSourceMode } from './useTmsLoadsEditor'

export const useTmsCountryOptions = (companyId: string) => {
  const { activeLanguage } = useLocalize()
  const lang = activeLanguage?.code || 'pl'
  const { data: countries = [] } = useQueryCountries({ companyId })

  const countryOptions = React.useMemo(
    () =>
      countries.map((c) => ({
        label: translateCountryName(c.name, lang),
        value: c.name,
      })),
    [countries, lang],
  )

  return { countries, countryOptions, lang }
}

interface TmsSourceFilterProps {
  companyId: string
  name: string
  driverName?: string
  dateFrom: string
  dateTo: string
  onDateFromChange: (value: string) => void
  onDateToChange: (value: string) => void
  mode: TmsSourceMode
  onModeChange: (mode: TmsSourceMode) => void
  vehicleId: string | null
  onVehicleChange: (vehicleId: string | null) => void
}

// Okres i źródło (kierowca albo pojazd) jak w WPF-owych oknach TMS.
export const TmsSourceFilter: React.FC<TmsSourceFilterProps> = ({
  companyId,
  name,
  driverName,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  mode,
  onModeChange,
  vehicleId,
  onVehicleChange,
}) => {
  const { t } = useLocalize()
  const { data: vehiclesData } = useQueryVehiclesList({ companyId })

  const vehicleOptions = React.useMemo(
    () =>
      ((vehiclesData as VehiclesResponse) || []).map((v) => ({
        label: v.registrationNumber ?? '',
        value: String(v.id),
      })),
    [vehiclesData],
  )

  return (
    <div className="tw-border-b tw-border-base-300 tw-bg-white tw-px-6 tw-py-4">
      <div className="tw-grid tw-grid-cols-1 tw-items-end tw-gap-4 md:tw-grid-cols-[1fr_1fr_auto]">
        <DateField
          label={t('newDelegation.dateFrom')}
          value={dateFrom}
          setValue={(value) => onDateFromChange(value || '')}
          placeholder="YYYY-MM-DD"
        />
        <DateField
          label={t('newDelegation.dateTo')}
          value={dateTo}
          setValue={(value) => onDateToChange(value || '')}
          placeholder="YYYY-MM-DD"
        />
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4 tw-pb-2">
          <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm tw-text-content-base">
            <input
              type="radio"
              name={name}
              className="tw-h-4 tw-w-4 tw-cursor-pointer tw-accent-primary"
              checked={mode === 'driver'}
              onChange={() => onModeChange('driver')}
            />
            {t('common.driver')}
            {driverName && (
              <span
                className={
                  mode === 'driver' ? 'tw-font-medium' : 'tw-opacity-50'
                }
              >
                {driverName}
              </span>
            )}
          </label>
          <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm tw-text-content-base">
            <input
              type="radio"
              name={name}
              className="tw-h-4 tw-w-4 tw-cursor-pointer tw-accent-primary"
              checked={mode === 'vehicle'}
              onChange={() => onModeChange('vehicle')}
            />
            {t('common.vehicle')}
          </label>
          <div className="tw-w-[200px]">
            <Dropdown
              compact
              searchable
              portalMenu
              disabled={mode !== 'vehicle'}
              options={vehicleOptions}
              value={vehicleId}
              placeholder={t('common.vehicle')}
              searchPlaceholder={t('menu.search')}
              onChange={(value) => onVehicleChange(value)}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
